import type { Project } from '@data/projects';

/** Plan rows used for seeker project card thumbnails */
export type PlansImageRow = {
	id: number;
	after_image_url?: string;
	before_image_url?: string;
};

const gardenerProjects = new Map<string, Project[]>();
const seekerProjects = new Map<number, Project[]>();
const seekerPlans = new Map<number, PlansImageRow[]>();
const planThumbs = new Map<number, string>();

export function buildGardenerProjectsSessionKey(
	user: { id: string; role: string; activeCompanyId?: number | string | null },
	companyScope: 'all_companies' | 'company',
	/** When admin uses “View as user”, keep cache keys distinct per subject + scope */
	adminViewAsFragment?: string | null
): string {
	if (user.role === 'admin') {
		if (adminViewAsFragment) {
			return `admin:${user.id}:as:${adminViewAsFragment}`;
		}
		return `admin:${user.id}`;
	}
	return `${user.role}:${user.id}:${companyScope}:${user.activeCompanyId ?? ''}`;
}

export function readGardenerProjectsSession(key: string): Project[] | undefined {
	return gardenerProjects.get(key);
}

export function writeGardenerProjectsSession(key: string, projects: Project[]): void {
	gardenerProjects.set(key, projects);
}

export function readSeekerProjectsSession(userId: number): Project[] | undefined {
	return seekerProjects.get(userId);
}

export function writeSeekerProjectsSession(userId: number, projects: Project[]): void {
	seekerProjects.set(userId, projects);
}

export function readSeekerPlansSession(userId: number): PlansImageRow[] | undefined {
	return seekerPlans.get(userId);
}

export function writeSeekerPlansSession(userId: number, plans: PlansImageRow[]): void {
	seekerPlans.set(userId, plans);
}

export function readPlanThumbSession(planId: number): string | undefined {
	return planThumbs.get(planId);
}

export function writePlanThumbSession(planId: number, url: string): void {
	planThumbs.set(planId, url);
}

/** Clear in-memory lists when logging out or switching account */
export function clearProjectsSessionCaches(): void {
	gardenerProjects.clear();
	seekerProjects.clear();
	seekerPlans.clear();
	planThumbs.clear();
}
